
import Vue from 'vue';
import {
  FileDocument, VForm, Label, User, Realm, fileDocumentLicenseOptions, FileDocumentStatusHistory,
  languageOptions,
} from '@/model';
import LabelPicker from '@/components/LabelPicker.vue';
import { notify, notifyError } from '@/store/notification';
import { RouteNames } from '@/router/route-names';
import labels from '@/store/labels';
import users from '@/store/users';
import { notEmpty } from '@/util/array-util';
import api from '@/rest-api';
import DatePicker from '@/components/DatePicker.vue';
import YearPicker from '@/components/YearPicker.vue';
import UserPicker from '@/components/UserPicker.vue';
import PublisherPicker from '@/components/PublisherPicker.vue';
import RealmPicker from '@/components/RealmPicker.vue';
import realms from '@/store/realms';
import Progress from '@/components/Progress.vue';
import {
  documentLabelTypes as configuredDocumentLabelTypes,
  documentPublishedFormat as configuredDocumentPublishedFormat,
  documentPublishedLabel as configuredDocumentPublishedLabel,
  documentStatusOptions,
  fileDocumentDisplayName,
} from '@/store/ui-config';
import StatusHistoryTimeline from './StatusHistoryTimeline.vue';

export default Vue.extend({
  name: 'DocumentEditor',
  components: {
    LabelPicker,
    UserPicker,
    PublisherPicker,
    DatePicker,
    YearPicker,
    RealmPicker,
    Progress,
    StatusHistoryTimeline,
  },
  props: {
    value: { type: Object as () => FileDocument | undefined, required: false },
    viewable: { type: Boolean, default: false },
    hideLabelSettings: { type: Boolean, default: false },
  },

  data() {
    return {
      loading: false,
      editItem: {} as Partial<FileDocument>,
      editLabels: [] as Label[],
      editUser: undefined as User | undefined,
      editRealm: undefined as Realm | undefined,
      uploadFile: undefined as File | undefined,
      translate: false,
      dateMenu: false,
    };
  },

  watch: {
    value: {
      immediate: true,
      handler(document: FileDocument | undefined) {
        if (document) {
          this.editItem = { ...document };
          this.editLabels = document.labels
            .map((id) => labels.getters.lookup.get(id))
            .filter(notEmpty);
          this.editUser = users.getters.lookup.get(document.userId);
          this.editRealm = realms.getters.lookup.get(document.realmId);
        } else {
          this.new();
        }
      },
    },
    uploadFile(f: File | undefined) {
      if (f?.name && !this.editItem.name) {
        this.editItem = { ...this.editItem, name: f.name };
      }
    },
  },

  computed: {
    editForm(): VForm {
      return this.$refs.editForm as VForm;
    },
    resettable(): boolean {
      return !this.value?.id;
    },
    preview(): string | undefined {
      return this.editItem ? fileDocumentDisplayName(this.editItem) : undefined;
    },
    showInheritedLicense(): boolean {
      return this.editItem?.license === undefined && !!this.editItem?.publisher?.license;
    },
    licenseOptions(): typeof fileDocumentLicenseOptions | undefined {
      return fileDocumentLicenseOptions;
    },
    languageOptions(): typeof languageOptions {
      return languageOptions;
    },
    documentPublishedFormat(): string {
      return configuredDocumentPublishedFormat();
    },
    documentPublishedLabel(): string {
      return configuredDocumentPublishedLabel();
    },
    documentLabelTypes(): readonly string[] {
      return configuredDocumentLabelTypes();
    },
  },

  methods: {
    fileDocumentStatusOptions(status: FileDocument['status']) {
      return documentStatusOptions([status]);
    },
    close() {
      this.$emit('close');
    },
    reset() {
      this.$emit('reset');
    },
    new() {
      this.editItem = { id: 0 };
      this.editLabels = [];
      this.editUser = undefined;
      const realmId = realms.getters.main;
      this.editRealm = realmId ? realms.getters.lookup.get(realmId) : undefined;
      this.uploadFile = undefined;
      this.translate = false;
    },
    showDocumentResult(doc: FileDocument) {
      this.$router.push({ name: RouteNames.DocumentResult, params: { documentId: `${doc.id}` } });
    },
    async save(view: boolean) {
      if (this.editForm.validate()) {
        const file = this.uploadFile;
        this.loading = true;
        try {
          const savedLabels = await Promise.all(this.editLabels.map((l) => (l.id < 1 ? api.saveLabel(l) : Promise.resolve(l))));
          const desc = file ? await api.uploadFile(file) : this.editItem.file;
          const userId = this.editUser?.id;
          const realmId = this.editRealm?.id;
          const saved = await api.saveDocument({
            ...this.editItem, file: desc, labels: savedLabels.map((l) => l.id), userId, realmId,
          }, this.translate);
          if (this.resettable) {
            this.new();
          }
          if (view) {
            this.showDocumentResult(saved);
          }
          notify('Document Saved');
          this.loading = false;
          this.$emit('saved', saved);
        } catch {
          notifyError('Could not save document');
        }
      }
    },
    history(): Promise<FileDocumentStatusHistory[]> {
      if (this.editItem.id) {
        return api.documentStatusHistory(this.editItem.id);
      }
      return Promise.resolve([]);
    },
  },

});
